/**
 * Scale factor for symbols drawn on the maplibre canvas (cursor, charging
 * station pins, route markers).
 *
 * These live on the WebGL canvas, so the CSS type scale in mobile.scss cannot
 * reach them. A CSS pixel is physically ~1.6-1.8x smaller on a phone than on a
 * desktop monitor at 100% zoom, which leaves map symbols reading far too small
 * on a real handset even though DevTools emulation looks correct.
 *
 * Matched on pointer type rather than viewport width, mirroring mobile.scss: a
 * narrow desktop window has a mouse and needs no bump.
 *
 * Symbols are always scaled by growing the source raster (canvas dimensions or
 * Image width/height) and leaving `icon-size` alone, so the oversample ratio
 * that keeps them crisp on HiDPI screens is preserved. Only where the raster is
 * not ours to resize does the multiplier go on `icon-size`.
 *
 * Route direction arrows and turn-arrow heads are deliberately NOT scaled here:
 * they are sized to match the route line width and would desynchronise from it.
 */

const TOUCH_SYMBOL_SCALE = 1.25;
const POINTER_QUERY = '(pointer: coarse) and (hover: none)';

function isCoarsePointer(): boolean {
  // Guard for SSR / non-browser platforms, where matchMedia is absent.
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia(POINTER_QUERY).matches;
}

/**
 * Live current scale. Unlike CSS media queries (which the browser re-evaluates
 * natively), map symbols are rasterized once and cached, so this value is kept
 * up to date and consumers must rebuild their symbols on change — see
 * onMapSymbolScaleChange(). Tuning the touch size is still a single edit to
 * TOUCH_SYMBOL_SCALE above.
 */
let currentScale: number = isCoarsePointer() ? TOUCH_SYMBOL_SCALE : 1;

const changeListeners = new Set<(scale: number) => void>();

// Track pointer-type changes so map symbols can resize the way CSS already does.
// Real triggers: a 2-in-1 attaching/detaching a keyboard, or the browser's
// device-emulation toggle while debugging.
if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
  const mediaQuery = window.matchMedia(POINTER_QUERY);
  const onPointerChange = (): void => {
    const next = mediaQuery.matches ? TOUCH_SYMBOL_SCALE : 1;
    if (next === currentScale) {
      return;
    }
    currentScale = next;
    changeListeners.forEach((listener) => listener(currentScale));
  };
  // addEventListener is the modern API; older Safari only exposes addListener.
  if (typeof mediaQuery.addEventListener === 'function') {
    mediaQuery.addEventListener('change', onPointerChange);
  } else if (typeof (mediaQuery as unknown as { addListener?: unknown }).addListener === 'function') {
    (mediaQuery as unknown as { addListener: (cb: () => void) => void }).addListener(onPointerChange);
  }
}

/** Current touch/desktop symbol scale (1 on desktop, TOUCH_SYMBOL_SCALE on touch). */
export function getMapSymbolScale(): number {
  return currentScale;
}

/**
 * Subscribe to pointer-type changes. The callback fires with the new scale
 * whenever it flips between desktop and touch; use it to rebuild cached map
 * symbols. Returns an unsubscribe function.
 */
export function onMapSymbolScaleChange(callback: (scale: number) => void): () => void {
  changeListeners.add(callback);
  return () => changeListeners.delete(callback);
}

/** Scale a raster dimension (canvas or Image width/height) to a whole pixel. */
export function scaleRaster(px: number): number {
  return Math.round(px * currentScale);
}

/** Scale a maplibre `icon-size` value, kept to 3 decimals for readable styles. */
export function scaleIconSize(size: number): number {
  return Math.round(size * currentScale * 1000) / 1000;
}
